/**
 * RhDeclarerAbsenceModal — déclarer une absence depuis son planning.
 *
 * C'est le geste qui manquait : avant, on cliquait sur un créneau et un modal
 * s'ouvrait pour déclarer une absence avec son type et son justificatif. Le
 * chemin avait disparu (la fonction, elle, n'a jamais été supprimée : les
 * tables rh_absences / pièce jointe / workflow sont intactes).
 *
 * Les jours sélectionnés dans la grille pré-remplissent les dates : sélectionner
 * lundi à jeudi puis déclarer évite de cliquer chaque créneau de 2 h.
 *
 * La demande entre dans le workflow de validation EXISTANT (mig 196) : selon le
 * réglage de la commune, elle part au manager, ou au manager pour avis puis à
 * la RH / au maire. Rien n'est modifié de ce côté.
 */
import { useState } from 'react';
import { api, ApiException } from '@/lib/api';
import { Button, Alert } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { AlertCircle, Send } from 'lucide-react';
import { RH_ABSENCE_TYPES } from '@roronoa-collectivites/shared-types';
import type { RhAbsenceTypeDemande } from '@roronoa-collectivites/shared-types';

const INPUT_CLS =
  'h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3 text-ink ' +
  'focus:border-rh focus:outline-none focus:ring-2 focus:ring-rh';

export function RhDeclarerAbsenceModal({
  joursPreselectionnes, onClose, onDeclare,
}: {
  /** Jours cochés dans la grille (YYYY-MM-DD). Vide = saisie libre. */
  joursPreselectionnes: string[];
  onClose: () => void;
  onDeclare: () => void;
}) {
  const tries = [...joursPreselectionnes].sort();
  const aujourdhui = new Date().toISOString().slice(0, 10);

  const [dateDebut, setDateDebut] = useState(tries[0] ?? aujourdhui);
  const [dateFin, setDateFin] = useState(tries[tries.length - 1] ?? aujourdhui);
  const [demiDebut, setDemiDebut] = useState(false);
  const [demiFin, setDemiFin] = useState(false);
  const [type, setType] = useState<RhAbsenceTypeDemande>('conges_payes');
  const [motif, setMotif] = useState('');
  const [piece, setPiece] = useState<File | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Un arrêt de travail sans justificatif, c'est un dossier incomplet : on le
  // signale sans bloquer (la RH peut le réclamer ensuite).
  const justificatifAttendu = type === 'maladie' || type === 'accident_travail';

  async function envoyer() {
    if (dateFin < dateDebut) { setError('La date de fin doit suivre la date de début.'); return; }
    setEnvoi(true); setError(null);
    try {
      await api.rh.demanderAbsence(
        {
          dateDebut, dateFin,
          demiDebut: demiDebut || undefined,
          demiFin: demiFin || undefined,
          typeDemande: type,
          motif: motif.trim() || undefined,
        },
        piece ?? undefined
      );
      onDeclare();
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Envoi impossible');
      setEnvoi(false);
    }
  }

  return (
    <Dialog open onClose={onClose} title="Déclarer une absence">
      <div className="space-y-4">
        {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}

        {tries.length > 1 && (
          <p className="text-xs text-ink-subtle">
            {tries.length} jours sélectionnés dans votre planning.
          </p>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-sm">
            <span className="mb-1 block text-xs text-ink-muted">Du</span>
            <input type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} className={INPUT_CLS} />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-xs text-ink-muted">Au</span>
            <input type="date" value={dateFin} onChange={(e) => setDateFin(e.target.value)} className={INPUT_CLS} />
          </label>
        </div>

        <div className="flex flex-wrap gap-4 text-sm">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={demiDebut} onChange={(e) => setDemiDebut(e.target.checked)} />
            Commence l'après-midi
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={demiFin} onChange={(e) => setDemiFin(e.target.checked)} />
            Finit le matin
          </label>
        </div>

        <label className="block text-sm">
          <span className="mb-1 block text-xs text-ink-muted">Type</span>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as RhAbsenceTypeDemande)}
            className={INPUT_CLS}
          >
            {RH_ABSENCE_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </label>

        <label className="block text-sm">
          <span className="mb-1 block text-xs text-ink-muted">Motif (facultatif)</span>
          <input value={motif} onChange={(e) => setMotif(e.target.value)} maxLength={2000} className={INPUT_CLS} />
        </label>

        <label className="block text-sm">
          <span className="mb-1 block text-xs text-ink-muted">
            Justificatif (PDF ou photo{justificatifAttendu ? '' : ' — facultatif'})
          </span>
          <input
            type="file"
            accept="application/pdf,image/jpeg,image/png,image/heic"
            onChange={(e) => setPiece(e.target.files?.[0] ?? null)}
            className="text-sm text-ink-muted file:mr-3 file:rounded-r-md file:border-0 file:bg-rh-tint file:px-3 file:py-2 file:text-rh-fg"
          />
          {justificatifAttendu && !piece && (
            <span className="mt-1 block text-xs text-warning">
              Un arrêt est généralement attendu pour ce type d'absence — vous pourrez
              le transmettre plus tard si vous ne l'avez pas encore.
            </span>
          )}
        </label>

        <p className="text-xs text-ink-subtle">
          Votre demande sera transmise pour validation selon les règles de la commune.
        </p>

        <div className="flex justify-end gap-2 border-t border-border pt-3">
          <Button variant="secondary" size="sm" onClick={onClose}>Annuler</Button>
          <Button variant="primary" size="sm" disabled={envoi} onClick={() => void envoyer()}>
            <Send className="h-4 w-4" aria-hidden="true" />
            {envoi ? 'Envoi…' : 'Envoyer'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
